import { supabase } from './supabase';

export type StudentRecord = {
  id: string;
  parent_id: string;
  first_name: string;
  last_name: string;
  homework_streak: number;
  learning_plan: string | null;
  drive_url: string | null;
  drive_url_2: string | null;
};

const studentColumns = 'id, parent_id, first_name, last_name, homework_streak, learning_plan, drive_url, drive_url_2';

export async function listStudents(userId: string, userRole: 'admin' | 'parent'): Promise<StudentRecord[]> {
  let query = supabase
    .from('students')
    .select(studentColumns)
    .order('first_name');

  if (userRole === 'parent') query = query.eq('parent_id', userId);

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as StudentRecord[];
}

export async function saveHomeworkStreak(
  userRole: 'admin' | 'parent',
  studentId: string,
  expectedStreak: number,
  updatedStreak: number,
): Promise<StudentRecord> {
  if (userRole !== 'admin') throw new Error('Only administrators can update student records.');
  if (!Number.isInteger(updatedStreak) || updatedStreak < 0) {
    throw new Error('Homework streak must be a non-negative whole number.');
  }

  const { data, error } = await supabase
    .from('students')
    .update({ homework_streak: updatedStreak })
    .eq('id', studentId)
    .eq('homework_streak', expectedStreak)
    .select(studentColumns)
    .single();

  if (error) {
    if (error.code === 'PGRST116') throw new Error('This streak changed elsewhere. Reload the student and try again.');
    throw new Error(error.message);
  }
  return data as StudentRecord;
}

export async function saveLearningPlan(
  userRole: 'admin' | 'parent',
  studentId: string,
  expectedPlan: string | null,
  updatedPlan: string,
): Promise<StudentRecord> {
  if (userRole !== 'admin') throw new Error('Only administrators can update student records.');
  let query = supabase
    .from('students')
    .update({ learning_plan: updatedPlan })
    .eq('id', studentId);
  query = expectedPlan === null
    ? query.is('learning_plan', null)
    : query.eq('learning_plan', expectedPlan);
  const { data, error } = await query.select(studentColumns).single();

  if (error) {
    if (error.code === 'PGRST116') throw new Error('This program plan changed elsewhere. Reload the student before saving.');
    throw new Error(error.message);
  }
  return data as StudentRecord;
}